/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import {remove as removeDiacritics} from 'diacritics';
import PropTypes from 'prop-types';

import {setCurrentTask} from '../actions/task';
import ResizeableWindow from '../components/ResizeableWindow';
import LayerCatalogWidget from '../components/widgets/LayerCatalogWidget';
import LocaleUtils from '../utils/LocaleUtils';
import MiscUtils from '../utils/MiscUtils';
import Request from '../utils/Request';
import {registerSearchProvider, SearchResultType, unregisterSearchProvider} from '../utils/SearchProviders';

import './style/LayerCatalog.css';


/**
 * Displays a pre-configured catalog of external layers in a window.
 *
 * Configured through a catalog JSON containing a tree of external layer identifiers.
 *
 * For catalog entries containing a `wms` resource, you can specify `"asGroup": <true|false|"option">`, which behaves as follows:
 *
 * * `false`: imports the layer as a flat layer, without sublayer structure. This is the default behaviour for entries without sublayers if `asGroup` is not specified.
 * * `true`:  imports the layer as a group, with sublayer structure. If the catalog entry has sublayers, the sublayer structure will be filtered to only include these sublayers.
 * * `"option"`: imports the layer as a flat layer when clicking on the catalog entry, but displays an icon to optionally import the layer as a group layer. This is the default behaviour for entries with sublayers if `asGroup` is not specified.
 *
 * You can set a default fallback value for `asGroup` via the  `defaultAsGroupValue` `LayerCatalog` configuration option.
 *
 * Example:
 * ```json
 * {
 *   "catalog": [
 *     {
 *       "title": "Öffentlicher Verkehr swissTLMRegio",
 *       "resource": "wms:http://wms.geo.admin.ch#ch.swisstopo.vec200-transportation-oeffentliche-verkehr",
 *       "asGroup": true
 *     },
 *     {
 *       "title": "Gewässerschutz",
 *       "resource": "wms:https://geo.so.ch/api/wms#ch.so.afu.gewaesserschutz[50]"
 *     },
 *     {
 *       "title": "Landeskarten",
 *       "sublayers": [
 *         {
 *           "title": "Landeskarte 1:1 Million | LK1000",
 *           "resource": "wms:http://wms.geo.admin.ch#ch.swisstopo.pixelkarte-farbe-pk1000.noscale"
 *         },
 *         {
 *           "title": "Landeskarte 1:100`000 | LK100",
 *           "resource": "wms:http://wms.geo.admin.ch#ch.swisstopo.pixelkarte-farbe-pk100.noscale"
 *         }
 *       ]
 *     },
 *     {
 *       "title": "Edit Demo",
 *       "resource": "wms:https://demo.qwc.app/ows/qwc_demo#edit_demo",
 *       "sublayers": [{
 *         "title": "Edit Points",
 *         "resource": "wms:https://demo.qwc.app/ows/qwc_demo#edit_points"
 *       },
 *       {
 *         "title": "Edit Lines",
 *         "resource": "wms:https://demo.qwc.app/ows/qwc_demo#edit_lines"
 *       }],
 *       "asGroup": "option"
 *     }
 *   ]
 * }
 * ```
 */
class LayerCatalog extends React.Component {
    static propTypes = {
        active: PropTypes.bool,
        /** The URL to the catalog JSON file. */
        catalogUrl: PropTypes.string,
        /** Optional default value for `asGroup`, with possible values as documented above.*/
        defaultAsGroupValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        /** Default window geometry with size, position and docking status. Positive position values (including '0') are related to top (InitialY) and left (InitialX), negative values (including '-0') to bottom (InitialY) and right (InitialX). */
        geometry: PropTypes.shape({
            initialWidth: PropTypes.number,
            initialHeight: PropTypes.number,
            initialX: PropTypes.number,
            initialY: PropTypes.number,
            initiallyDocked: PropTypes.bool,
            side: PropTypes.string
        }),
        /** Whether to increase the indent size dynamically according to the current level (`true`) or keep the indent size constant (`false`). */
        levelBasedIndentSize: PropTypes.bool,
        /** Whether to register a search provider which allows searching catalog layers through the global search field. */
        registerCatalogSearchProvider: PropTypes.bool,
        setCurrentTask: PropTypes.func,
        /** Whether clicking the group name toggles the WMS group rather than loading the grouped layer. */
        toggleGroupOnClick: PropTypes.bool
    };
    static defaultProps = {
        geometry: {
            initialWidth: 320,
            initialHeight: 320,
            initialX: 0,
            initialY: 0,
            initiallyDocked: false,
            side: 'left'
        },
        levelBasedIndentSize: true,
        registerCatalogSearchProvider: true,
        toggleGroupOnClick: false
    };
    state = {
        catalog: null,
        visible: false
    };
    componentDidMount() {
        if (this.props.catalogUrl) {
            Request.get(MiscUtils.resolveAssetsPath(this.props.catalogUrl)).then(response => {
                this.setState({
                    catalog: response.data.catalog || []
                });
            }).catch(e => {
                this.setState({catalog: []});
                // eslint-disable-next-line
                console.warn("Failed to load catalog: " + e);
            });
        }
        if (this.props.registerCatalogSearchProvider) {
            registerSearchProvider("catalogsearch", {
                label: LocaleUtils.tr("layercatalog.windowtitle"),
                onSearch: this.searchCatalog
            });
        }
    }
    componentWillUnmount() {
        if (this.props.registerCatalogSearchProvider) {
            unregisterSearchProvider("catalogsearch");
        }
    }
    componentDidUpdate(prevProps) {
        if (this.props.active && !prevProps.active) {
            this.setState({visible: true});
            this.props.setCurrentTask("LayerTree");
        }
    }
    render() {
        if (!this.state.visible) {
            return null;
        }
        return (
            <ResizeableWindow dockable={this.props.geometry.side} icon="catalog"
                initialHeight={this.props.geometry.initialHeight} initialWidth={this.props.geometry.initialWidth}
                initialX={this.props.geometry.initialX} initialY={this.props.geometry.initialY}
                initiallyDocked={this.props.geometry.initiallyDocked}
                onClose={this.onClose} title={LocaleUtils.tr("layercatalog.windowtitle")}
            >
                <div className="layer-catalog">
                    <LayerCatalogWidget catalog={this.state.catalog} defaultAsGroupValue={this.props.defaultAsGroupValue} levelBasedIndentSize={this.props.levelBasedIndentSize} pendingRequests={0} toggleGroupOnClick={this.props.toggleGroupOnClick} />
                </div>
            </ResizeableWindow>
        );
    }
    onClose = () => {
        this.setState({visible: false});
    };
    searchCatalog = (text, searchParams, callback) => {
        const filter = new RegExp(removeDiacritics(text).replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&"), "i");
        const results = [];
        if (this.state.catalog) {
            this.searchCatalogGroup(results, filter, this.state.catalog);
        }
        if (results.length > 0) {
            callback({results: [{
                id: "cataloglayers",
                title: LocaleUtils.tr("layercatalog.windowtitle"),
                type: SearchResultType.EXTERNALLAYER,
                items: results
            }]});
        } else {
            callback({results: []});
        }
    };
    searchCatalogGroup = (results, filter, group) => {
        group.forEach(entry => {
            const type = entry.resource ? entry.resource.slice(0, entry.resource.indexOf(":")) : entry.type;
            if (entry.sublayers) {
                this.searchCatalogGroup(results, filter, entry.sublayers);
            }
            if (entry.sublayers && type !== "wms") {
                return;
            }
            if (removeDiacritics(entry.title).match(filter)) {
                results.push({
                    id: "cataloglayer:" + results.length,
                    text: entry.title,
                    layer: entry,
                    sublayers: entry.sublayers?.map?.((sublayer, idx) => ({
                        id: "cataloglayer:" + results.length + ":" + idx,
                        text: sublayer.title,
                        layer: sublayer
                    }))
                });
            }
        });
    };
}

export default connect(state => ({
    active: state.task.id === "LayerCatalog"
}), {
    setCurrentTask: setCurrentTask
})(LayerCatalog);
